<template>
  <div
    v-on-click-outside="onClickOutside"
    :class="[
      'filter-block',
      { open },
      { 'filter-block-set': isSetFilter, 'not-active': allNotActive },
    ]"
  >
    <div ref="wrapper" class="wrapper">
      <div class="filter-block-header" tabindex="0" @click="onHeaderClick">
        <slot name="header" :open="open">
          <span class="title-header body-m-regular">{{
            tempTitle || title
          }}</span>
        </slot>
      </div>
      <div
        ref="listBody"
        class="filter-block-list-wrapper"
        @click="onClickListWrap"
      >
        <div class="filter-block-list-effect">
          <div class="popup-title text-h3-bold-up">
            Фильтры
            <div class="icon-close" @click="open = false" />
          </div>
          <div class="filter-block-list-header">
            <div class="filter-block-list-title body-m-regular">
              {{ settings?.name }}
            </div>
            <button
              type="button"
              :disabled="selectedIds.length === 0 && !isSetFilter"
              class="reset-button body-m-regular"
              @click="onReset"
            >
              Сбросить
            </button>
          </div>
          <div v-if="settings.allowed_values?.length > 5" class="search-wrap">
            <UiPInput
              v-model="search"
              :value="search"
              class="search"
              placeholder="Найти"
              name="search"
              @input="(v) => (search = v)"
            >
              <template #left>
                <img
                  class="icon"
                  src="/static/images/search.svg"
                  alt="search"
                />
              </template>
            </UiPInput>
          </div>
          <PerfectScrollbar
            class="filter-block-list"
            :options="{ wheelPropagation: false }"
            @click.stop
          >
            <div
              v-for="(item, index) in filterList"
              :key="index.toString()"
              :class="['select-item', 'body-m-regular']"
              @click="onSelect(item)"
            >
              <slot name="item" :item="item">
                <UiPCheckbox
                  class="item-checkbox"
                  :value="selectedIds.includes(item.id)"
                  :label="item?.name"
                  :active="item?.active"
                />
              </slot>
            </div>
          </PerfectScrollbar>
          <div v-if="search && filterList.length === 0" class="no-search">
            Ничего не найдено
          </div>
          <div class="button-wrap body-m-regular">
            <ui-ui-button
              size="medium"
              font-class="body-m-regular"
              :disabled="selectedIds.length === 0 && !isSetFilter"
              :type="
                selectedIds.length === 0 && !isSetFilter ? 'neutral' : 'primary'
              "
              :resize="true"
              @click="onSetFilters"
            >
              Применить
            </ui-ui-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { vOnClickOutside } from '@vueuse/components';
import type { EnumFilter, EnumAllowed, EventPatch } from '~/interfaces/api';
import { useMediaQueryStore } from '~/stores/mediaQuery';

const props = defineProps({
  settings: {
    type: Object as () => EnumFilter,
    required: true,
  },
  list: {
    type: Array,
    default: () => [],
  },
  setFilters: {
    type: String,
    default: '',
  },
  value: {
    type: Array as () => number[],
    default: () => [],
  },
});
const emit = defineEmits(['onEnumChange', 'setFilters']);
const mediaStore = useMediaQueryStore();
const isTablet = computed(
  () =>
    mediaStore.isMobile || mediaStore.isEs || mediaStore.isSm || mediaStore.isMd
);
// открытие закрытие дропдауна
const open = ref<boolean>(false);
// строка поиска
const search = ref<string>('');

// временное сохранение текущего заголовка, что б не менялся при выборе фильтра, пока не закроют меню
const tempTitle = ref<string>('');

const listBody = ref<HTMLElement>();
const wrapper = ref<HTMLElement>();

// применялся фильтр или нет
const isSetFilter = ref<boolean>(false);
// выбранные элементы

const selected = computed(() =>
  props.settings.allowed_values.filter((item: EnumAllowed) =>
    props.value.find((vItem: number) => vItem === item.id)
  )
);

const allNotActive = computed(() => {
  if (!props.settings.allowed_values) return false;
  return (
    props.settings.allowed_values?.length &&
    props.settings.allowed_values.filter((item: EnumAllowed) => item.active)
      .length === 0
  );
});

// Заголовки выбранных элементов
const selectedName = computed(() =>
  selected.value.map((item: EnumAllowed) => item?.name)
);

// заголовок  "кнопки"
const title = computed(() =>
  isSetFilter.value
    ? selectedName.value.join(', ') || props.settings?.name
    : props.settings?.name
);

// ИД выбранных элементов
const selectedIds = computed(() =>
  selected.value.map((item: EnumAllowed) => item.id)
);

// список всех элементов с учетом поиска внутри дропдауна
const filterList = computed(() => {
  if (!search.value) return props.settings.allowed_values;
  return props.settings.allowed_values.filter((item: EnumAllowed) =>
    item?.name.toUpperCase().includes(search.value.toUpperCase())
  );
});

const popupLeft = computed(() => {
  if (!isTablet.value && open.value && wrapper.value && listBody.value) {
    const { width: listWidth } = listBody.value.getBoundingClientRect();
    const { width: wrapperWidth, left: wrapperLeft } =
      wrapper.value.getBoundingClientRect();

    if (wrapperLeft + listWidth >= document.body.clientWidth) {
      return `-${listWidth - wrapperWidth}px`;
    }
  }
  return '0';
});

watch(
  () => props.setFilters,
  (value) => {
    if (value) {
      isSetFilter.value = selectedName.value.length > 0;
    }
  }
);
onMounted(() => {
  if (selectedName.value.length > 0) {
    isSetFilter.value = true;
  }
});

// открытие/закрытие модалки в таблет версии
const onHeaderClick = (): void => {
  if (isSetFilter.value && !open.value && selectedName.value.length > 0) {
    tempTitle.value = selectedName.value.join(', ');
  }
  if (isTablet.value) onPopup(true);
  else open.value = !open.value;
};
// закрытие дропдауна в десктоп версии
const onClickOutside = (): void => {
  if (!isTablet.value) open.value = false;
};

const onSetFilters = (): void => {
  if (props.value?.length === 0) {
    onReset();
  } else {
    onPopup(false);
    search.value = '';
    isSetFilter.value = true;
    emit('setFilters');
  }
};
const onReset = (): void => {
  onPopup(false);
  search.value = '';
  isSetFilter.value = false;
  emit('onEnumChange', {
    id: props.settings.id,
    val: [],
    queryVal: undefined,
    changeIn: 'filters',
  });
  emit('setFilters');
};
const onPopup = (state: boolean): void => {
  if (state) {
    if (isSetFilter.value && selectedName.value.length > 0) {
      tempTitle.value = selectedName.value.join(', ');
    }
    open.value = true;
    nextTick(() => {
      if (listBody.value) {
        const scrollWidth =
          listBody.value?.offsetWidth - listBody.value?.clientWidth;
        document.body.classList.add('tablet-overflow-hidden-grid');
        document.body.style.paddingRight = scrollWidth + 'px';
      }
    });
  } else {
    open.value = false;
    tempTitle.value = '';
    open.value = false;
    document.body.classList.remove('tablet-overflow-hidden-grid');
    document.body.style.paddingRight = '';
  }
};

const onSelect = (item: EnumAllowed): void => {
  const value = selectedIds.value.includes(item.id)
    ? props.value.filter((itemArr: number) => itemArr !== item.id)
    : [...props.value, item.id];
  emit('onEnumChange', {
    id: props.settings.id,
    val: value,
    queryVal: value.join(','),
    changeIn: 'filters',
  });
};

const onClickListWrap = (e: EventPatch) => {
  const className = 'filter-block-list-wrapper';
  const target = e.target as HTMLElement;
  if (
    (e && 'path' in e && e.path[0] && e?.path[0]?.className === className) ||
    // ios safari fix
    target.className === className
  )
    onPopup(false);
};
watch(
  () => open.value,
  (v) => {
    if (v) {
      nextTick(() => {
        if (listBody.value) {
          document.body.classList.add('tablet-overflow-hidden');
        }
      });
    } else {
      document.body.classList.remove('tablet-overflow-hidden');
    }
  }
);
</script>

<style scoped lang="scss">
.filter-block {
  position: relative;
  width: auto;
  max-width: 250px;
  display: inline-block;
  background: $achromatic-card-bg;
  border-radius: 8px;
  border: 1px solid $achromatic-card-bg;
  @include lg-block {
    &:hover {
      border: 1px solid $light-blue;
    }
  }

  &.wrapper {
    display: flex;
    flex-flow: column nowrap;
  }

  &-header {
    width: auto;
    cursor: pointer;
    user-select: none;
    outline: none;
    box-shadow: none;
    position: relative;
    //border-radius: 4px;
    padding: 7px 12px;
    display: flex;
    align-items: center;
    color: $achromatic-black;

    .title-header {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &:after {
      content: '';
      display: block;
      mask: url('/static/images/icon/triangle-filter.svg') center / contain
        no-repeat;
      width: 16px;
      height: 16px;
      margin-left: 4px;
      background-color: $achromatic-black;
      flex-shrink: 0;
      transform: translateY(-1px);
      transition: transform 0.3s ease-in;
      @include lg-block {
        width: 20px;
        height: 20px;
      }
    }

    &:active {
      background: $light-blue;
      border-radius: 8px;
    }

    &:hover {
      @include lg-block {
        background: $light-blue;
      }
    }
  }

  .search-wrap {
    padding: 8px 12px;
  }

  .no-search {
    padding: 0 12px;
  }

  .search {
    height: 36px;

    :deep(.p-input) {
      padding: 7px 11px;
    }

    .icon {
      padding-right: 8px;
      object-fit: contain;
      height: 20px;
    }
  }

  .filter-block-list-wrapper {
    z-index: 20;
    position: fixed;
    bottom: 0;
    top: 35px;
    left: 0;
    width: 100vw;
    background: rgba(0, 0, 0, 0);
    height: 0;
    overflow-y: scroll;
    align-self: center;
    transition: background-color 0.2s;
    border-width: 0;
    box-shadow: 0 8px 16px rgba(51, 51, 51, 0.2);
    border-radius: 4px;

    .filter-block-list-effect {
      border-radius: 4px;
      background-color: $white;
      @include lg-block {
        transform: translateY(100%);
      }
    }

    .filter-block-list-header {
      margin-top: 16px;
      display: flex;
      justify-content: space-between;
      padding: 0 12px;
      background: $white;
      @include lg-block {
        margin-top: 0;
      }
    }

    .filter-block-list-title {
      max-width: calc(100% - 65px);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      letter-spacing: 0.01em;
      color: $achromatic-black;
      @include lg-block {
        color: $achromatic-disabled;
      }
    }

    .reset-button {
      max-width: 65px;
      cursor: pointer;
      background: none;
      border: none;
      padding: 0;
      outline: none;
      color: $achromatic-disabled;

      &:disabled {
        color: #bebebe;
        cursor: default;
      }
    }

    .popup-title {
      margin-top: 12px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0 12px;
      position: static;
      top: 0;
      z-index: 1;
      background-color: $white;

      .icon-close {
        width: 20px;
        height: 20px;
        cursor: pointer;
        mask: url('/static/images/closeModal.svg') center / cover no-repeat;
        background-color: $achromatic-black;

        &:hover {
          background-color: $main-cta;
        }

        &:active {
          background-color: $hover-cta;
        }
      }

      @include lg-block {
        display: none;
      }
    }

    .filter-block-list {
      padding-top: 8px;
      user-select: none;
      display: flex;
      flex-flow: column nowrap;
      width: 100%;
      max-height: 250px;
      background: $white;
      @include lg-block {
        background: initial;
      }

      .select-item {
        cursor: pointer;
        padding: 6px 12px;
        color: $achromatic-black;
        border-radius: 0;

        .item-checkbox {
          pointer-events: none;
        }

        &.active {
          justify-content: flex-start;
        }

        @include lg-block {
          border-radius: 4px;
          &:hover {
            background-color: $light-blue;
          }
        }
      }
    }

    .button-wrap {
      padding: 10px 12px 12px;
      background: $white;
      @include lg-block {
        padding-top: 12px;
      }
    }

    @include lg-block {
      position: absolute;
      width: 316px;
      overflow: hidden;
      background: $white;
    }
  }

  &.open {
    .filter-block-header {
      background: $light-blue;
      border-radius: 8px;

      &:after {
        transform: rotate(180deg) translateY(0px);
      }
    }
  }

  &-set {
    border: 1px solid $main-cta;
  }

  &-list-wrapper {
    opacity: 1;

    .filter-block-list {
      border-radius: 0;
    }

    transition: opacity 0.2s;
    top: 100%;
    @include lg-block {
      opacity: 0;
      .filter-block-list {
        border-radius: 4px;
      }
    }
  }

  &.open {
    .filter-block-list-wrapper {
      right: 0;
      left: v-bind(popupLeft);
      top: 0;
      bottom: 0;
      padding-top: 98px;
      height: var(--fullvh);
      opacity: 1;
      z-index: 100;
      overflow: initial;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      align-items: stretch;
      background-color: rgba(0, 0, 0, 0.6);
      transition: background-color 0.2s;

      .filter-block-list-effect {
        transform: translateY(0);
      }

      @include lg-block {
        top: 35px;
        bottom: initial;
        background-color: #fff;
        border: 1px solid $achromatic-hover;
        padding-top: 8px;
        height: auto;
        opacity: 1;
        z-index: 40;
        overflow: initial;
      }
    }
  }
}

.not-active {
  .filter-block-header {
    color: $button-disabled;

    &:after {
      background-color: $button-disabled;
    }
  }
}
</style>
